'use strict';

import Util from '../util/Plaintext.Util';

export default class Tooltip {
  constructor(el, props) {
    el.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
    el.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
    el.addEventListener('click', this.handleClick.bind(this));
    el.addEventListener('touchstart', this.handleClick.bind(this));
    el.addEventListener('keydown', this.handleEscape.bind(this));
    this.el = el;
    
    this.open = false;
    this.fadeoutDelayHandle = null;
    
    this.props = Object.assign({}, Tooltip.DEFAULT_PROPS, props);
    
    // Build and place tooltip element
    this.getTooltipEl();
    
    this.fnResetPositionOnTransitionEnd = this.resetPositionOnTransitionEnd.bind(this);
  }
  
  handleMouseEnter() {
    this.softOpen();
  }
  
  handleMouseLeave() {
    this.softClose();
  }
  
  handleClick(e) {
    e.preventDefault();

    // If we're open, just ignore it.  This has the side-effect of preventing clicks on 
    // non-touch devices, since the act of hovering or focusing should mean the tooltip
    // is already open.
    if (this.open) {
      //window.setTimeout(() => this.getTooltipEl().focus(), 1);
      return;
    }
    
    this.hardOpen();
  }
  
  handleTooltipClick(e) {
    // Clicking on a tooltip?  Close it.  This is also protection against any unexpected
    // behaviours that leave a tooltip lying around.
    e.preventDefault();
    this.close();
  }
  
  handleEscape(e) {
    if (!this.open) { return; }

    const key = e.key;
    if ('Escape' === key) {
      e.preventDefault(); 
      this.close();
      return;
    }
  }
  
  /**
   * Shows the tooltip when:
   * 
   * a) The tooltip is not already shown AND
   * b) There is enough space for the tooltip to be shown
   *
   * Unlike a "hard show", the tooltip does not contain a close button.
   */
  softOpen() {
    if (this.open) { return; }

    const tooltipEl = this.getTooltipEl();
    if (!tooltipEl) { return; }

    tooltipEl.classList.add('nst-tooltip--soft');
    
    const position = this.getTooltipPosition();
    
    if (!position) { return; }
    
    this.showTooltip(position);
    this.cancelSoftClose();
    this.dispatchOpenEvent({ soft: true });
  }
  
  hardOpen() {
    if (this.open) { return; }

    const tooltipEl = this.getTooltipEl();
    if (!tooltipEl) { return; }

    tooltipEl.classList.remove('nst-tooltip--soft');
    
    const position = this.getTooltipPosition();
    if (!position) { return; }
    
    this.showTooltip(position);
    this.cancelSoftClose();
    
    tooltipEl.setAttribute('tabindex', '0');
    tooltipEl.focus();
    this.dispatchOpenEvent({ soft: false });
  }
  
  dispatchOpenEvent(additionalDetail) {
    const detail = Object.assign({ tooltip: this }, additionalDetail);
    const event = new CustomEvent('Tooltip.closed', { detail: detail, bubbles: true });
    this.el.dispatchEvent(event);
  }
  
  showTooltip(position) {
    const tooltipEl = this.getTooltipEl();
    tooltipEl.setAttribute('aria-hidden', 'false');
    tooltipEl.classList.add('nst-tooltip--open');
    this.open = true;

    this.placeTooltip(position);
  }

  hideTooltip() {
    const tooltipEl = this.getTooltipEl();

    tooltipEl.setAttribute('aria-hidden', 'true');
    tooltipEl.removeAttribute('tabindex', '');
    this.open = false;

    tooltipEl.classList.remove('nst-tooltip--open');
    
    // Prevent the tooltip from causing scrollbars
    tooltipEl.addEventListener('transitionend', this.fnResetPositionOnTransitionEnd, { once: true });
  }
  
  resetPositionOnTransitionEnd() {
    const tooltipEl = this.getTooltipEl();
    tooltipEl.style.top = 0;
    tooltipEl.style.left = 0;
  }

  placeTooltip(position) {
    const tooltipEl = this.tooltipEl;
    const style = tooltipEl.style;
    style.top = position.top + 'px';
    style.left = position.left + 'px';
    
    if (position.maxHeight) {
      style.maxHeight = position.maxHeight + 'px';
      
      // Set tabindex so keyboard users can scroll
      tooltipEl.setAttribute('tabindex', '0');
    }
    else {
      tooltipEl.setAttribute('tabindex', '-1');
      style.maxHeight = null;
    }
  }
  
  /**
   * @return Object: tooltip positioning coordinates for absolute positioning
   *
   * NOTE: Most of this logic is viewport-relative.  Any coordinates for absolute positioning are
   * called out by including "absolute" or "abs" in the variable name.
   * 
   * Tooltip Positioning Rules:
   *
   * 1) Examine available areas. These are the four rectangles that represent the space to the top/bottom
   *    of the triggering element, bounded by that element's container, the viewport.  Takes into consideration
   *    configured margins around both the element and the container/viewport.
   *
   * 2) Examine tooltip rectangle.  We do this by drawing the tooltip and getting the browser's rendered area.  There's
   *    no good way to do this without rendering due to styles, let alone things like glyph-widths on different browsers.
   *
   * 3) Test placement in the order defined in props.positionPreference.  The first one that fits is returned.  maxHeight is null.
   *
   * 4) If there is no perfect fit, shrink the tooltip to the highest available height and place it appropriately.  maxHeight is set.
   */
  getTooltipPosition() {
    const tooltipEl = this.getTooltipEl();
    tooltipEl.style.maxHeight = null;
    
    const tooltipRect = tooltipEl.getBoundingClientRect();
    const tooltipHeight = tooltipEl.scrollHeight;
    
    const containerEl = this.getContainerEl();
    const containerRect = containerEl.getBoundingClientRect();
    const containerBottomY = containerRect.y + containerEl.scrollHeight;
    
    const triggerEl = this.el;
    const triggerRect = triggerEl.getBoundingClientRect();
    const triggerBottomY = triggerRect.y + triggerEl.scrollHeight;

    // If the trigger is an inline element that spans lines, we need to get each individual
    // rectangle and see where the leftmost one is.  Then use that leftmost value to adjust
    // the absolute position.  This is because triggerEl.offsetLeft is the offset of the
    // *first* segment of the trigger.
    const triggerRects = triggerEl.getClientRects();
    
    let leftmostTriggerRect = triggerRects[0];

    Array.from(triggerRects).forEach(rect => {
      if (rect.left < leftmostTriggerRect.left) {
        leftmostTriggerRect = rect;
      }
    });

    const triggerAbsLeft = (triggerEl.offsetLeft - triggerRect.left);
    const triggerLeftmostRectDiff = triggerRects[0].left - leftmostTriggerRect.left;
    const triggerRect2AbsLeft = triggerAbsLeft - triggerLeftmostRectDiff;
    
    const triggerRect2AbsTop = triggerEl.offsetTop - triggerRect.top;

    let bestConstrainedResult = { top: 0, left: 0, maxHeight: 0 };
    let bestOffscreenConstrainedResult = { top: 0, left: 0, maxHeight: 0 };
    
    const containerMargin = this.props.containerMargin;
    const triggerMargin = this.props.triggerMargin;
    
    // Horizontal position: try to center above/below the trigger
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const halfTooltipWidth = tooltipRect.width / 2;
    
    let tooltipLeft = triggerCenterX - halfTooltipWidth;

    // Overflows left?  Push it all the way to the left
    if (tooltipLeft < containerRect.left + containerMargin) {
      tooltipLeft = containerRect.left + containerMargin;
    }
    // Overflows right?  Push it all the way to the right
    else if (tooltipLeft + tooltipRect.width > containerRect.right - containerMargin) {
      tooltipLeft = containerRect.left + containerRect.width - tooltipRect.width - containerMargin;
    }
    
    const tooltipAbsLeft = tooltipLeft + triggerRect2AbsLeft;
    
    // Vertical position: Find best available space
    for (let positionIdx in this.props.position) {
      const position = this.props.position[positionIdx];
      
      switch (position) {
        case Tooltip.POSITION_ABOVE:
          const containerTop = Math.max(containerRect.top + containerMargin, 0);
          const availableAbove = triggerRect.top - triggerMargin - containerTop;
          
          if (availableAbove >= tooltipHeight) {
            return {
              top: triggerRect.top - tooltipHeight - triggerMargin + triggerRect2AbsTop,
              left: tooltipAbsLeft,
              maxHeight: null
            }
          }
          
          if (availableAbove > bestConstrainedResult.maxHeight) {
            bestConstrainedResult = {
              top: triggerRect.top - availableAbove - triggerMargin + triggerRect2AbsTop,
              left: tooltipAbsLeft,
              maxHeight: availableAbove
            };
          }
          
          const containerAvailableAbove = triggerRect.top - containerRect.top - triggerMargin - containerMargin;
          
          if (containerAvailableAbove > bestOffscreenConstrainedResult.maxHeight) {
            bestOffscreenConstrainedResult = {
              top: triggerRect.top - containerAvailableAbove - triggerMargin + triggerRect2AbsTop,
              left: tooltipAbsLeft,
              maxHeight: containerAvailableAbove
            };
          }
          
          break;

        case Tooltip.POSITION_BELOW:
          // Convert from bottom-relative to absolute Y positioning
          const containerBottom = Math.min(containerBottomY - containerMargin, window.innerHeight);
          const availableBelow = containerBottom - triggerBottomY - triggerMargin;

          if (availableBelow >= tooltipHeight) {
            return {
              top: triggerBottomY + triggerMargin + triggerRect2AbsTop,
              left: tooltipAbsLeft,
              maxHeight: null
            }
          }
          
          if (availableBelow > bestConstrainedResult.maxHeight) {
            bestConstrainedResult = {
              top: triggerBottomY + triggerMargin + triggerRect2AbsTop,
              left: tooltipAbsLeft,
              maxHeight: availableBelow
            };
          }
          
          const containerAvailableBelow = containerBottomY - triggerBottomY - triggerMargin - containerMargin;

          if (containerAvailableBelow > bestOffscreenConstrainedResult.maxHeight) {
            bestOffscreenConstrainedResult = {
              top: triggerBottomY + this.props.triggerMargin + triggerRect2AbsTop,
              left: tooltipAbsLeft,
              maxHeight: containerAvailableBelow
            };
          }
          
          break;
      }
    }

    // If we can't possibly fit it in the screen, permit offscreen.
    if (bestConstrainedResult.maxHeight < this.props.minimumHeightBeforeGoingOffscreen) {
      if (bestOffscreenConstrainedResult.maxHeight > tooltipHeight) {
        bestOffscreenConstrainedResult.maxHeight = null;
      }

      return bestOffscreenConstrainedResult;
    }

    return bestConstrainedResult;
  }
  
  getTooltipEl() {
    if (!this.tooltipEl) { 
      this.tooltipEl = this.buildTooltipEl();
      
      if (this.tooltipEl) {
        this.el.after(this.tooltipEl);
        this.hideTooltip();
      }
    }
    
    return this.tooltipEl;
  }
  
  buildTooltipEl() {
    const target = this.getTargetEl();
    if (!target) { return null; }
    
    const tooltipEl = document.createElement('div');
    tooltipEl.classList.add('nst-tooltip');
    tooltipEl.setAttribute('role', 'note');
    
    tooltipEl.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
    tooltipEl.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
    tooltipEl.addEventListener('click', this.handleTooltipClick.bind(this));
    tooltipEl.addEventListener('keydown', this.handleEscape.bind(this));
    
    this.props.tooltipClasses.forEach(c => tooltipEl.classList.add(c));

    const contentEl = document.createElement('div');
    contentEl.classList.add('nst-tooltip__content');
    contentEl.innerHTML = target.innerHTML;
    tooltipEl.append(contentEl);
    
    const closeNote = this.renderCloseNote();
    closeNote && tooltipEl.append(closeNote);
    
    const eventDetail = { tooltip: this, triggerElement: this.el, tooltipElement: tooltipEl };
    const event = new CustomEvent('Tooltip.tooltipElementCreated', { detail: eventDetail, bubbles: true });
    this.el.dispatchEvent(event);

    return tooltipEl;
  }

  renderCloseNote() {  
    const closeNoteText = this.props.closeNote;
    const closeButtonHtml = this.props.closeButtonHtml;
    
    if (!closeNoteText && !closeButtonHtml) { return null; }
    
    if (closeButtonHtml) {
      const closeEl = document.createElement('button');
      closeEl.classList.add('nst-tooltip__close');
      closeEl.setAttribute('type', 'button');
      closeNoteText && closeEl.setAttribute('aria-label', closeNoteText);
      closeEl.addEventListener('click', () => this.close());
      closeEl.innerHTML = closeButtonHtml;
      
      return closeEl;
    }
    else {
      const closeNote = document.createElement('div');
      closeNote.classList.add('nst-tooltip__closeNote');
      closeNote.innerText = closeNoteText;
      return closeNote;
    }
  }

  
  getContainerEl() {
    if (!this.containerEl) {
      this.containerEl = this.el.closest(this.props.containerSelector);
    
      if (!this.containerEl) {
        console.error("Could not find ancestor with selector", this.props.containerSelector, " for tooltip for ", this.el);
      }
    }
    
    return this.containerEl;
  }
  
  getTargetEl() {
    const targetHref = (this.el && this.el.getAttribute('href')) || '';
    const targetId = targetHref.replaceAll('#', '');
    
    if (!targetId) { 
      console.error("Could not find target ID (href anchor) on element", this.el);
      return null;
    }
    
    const target = document.getElementById(targetId);

    if (!target) { 
      console.error("Could not find tooltip target with ID", target);
    }
    
    return target;
  }
   
  softClose() {
    const tooltipEl = this.getTooltipEl();
    if (!tooltipEl) { return; }
    
    this.cancelSoftClose();
    if (!tooltipEl.classList.contains('nst-tooltip--soft')) { return; }
    

    this.fadeoutDelayHandle = window.setTimeout(() => {
      // Hovered?  Cancel the fadeout.
      if (this.el.matches(':hover') || this.tooltipEl.matches(':hover')) {
        return;
      }

      this.close();
    }, this.props.fadeoutDelayMs);
  }
  
  cancelSoftClose() {
    this.fadeoutDelayHandle && window.clearTimeout(this.fadeoutDelayHandle);
    this.fadeoutDelayHandle = null;

    const tooltipEl = this.getTooltipEl();
    tooltipEl && tooltipEl.removeEventListener('transitionend', this.fnResetPositionOnTransitionEnd, { once: true });
  }
  
  close() {
    const tooltipEl = this.getTooltipEl();
    
    // Return focus to trigger
    if (tooltipEl.matches(':focus-within')) {
      this.el.focus();
    }
    
    this.hideTooltip();
    
    const eventDetail = { tooltip: this };
    const event = new CustomEvent('Tooltip.closed', { detail: eventDetail, bubbles: true });
    this.el.dispatchEvent(event);
  }
}

Tooltip.enable = (src, props) => {
  const elements = Util.elementsFrom(src);
  elements.forEach(el => new Tooltip(el, props));
}

Tooltip.POSITION_BELOW = 'below';
Tooltip.POSITION_ABOVE = 'above';

Tooltip.DEFAULT_PROPS = {
  // fadeDelayMs is the time between end-hover and fading.  This should be enough time
  // for a cursor to move from the trigger to the tooltip, since hovering over either
  // keeps the tooltip open.
  fadeoutDelayMs: 400,
  containerSelector: 'body',
  position: [ Tooltip.POSITION_BELOW, Tooltip.POSITION_ABOVE ],
  triggerMargin: 8,
  containerMargin: 8,
  closeNote: 'Click to dismiss',
  closeButtonHtml: null,
  minimumHeightBeforeGoingOffscreen: 400,
  tooltipClasses: []  
}

/**
 * Copyright 2023 Plaintext Inc.
 *
 * Permission to use, copy, modify, and distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 */
